import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { useLocalSwipeViews } from '../hooks/useSwipeViews';
import AddPedalForm from '../components/AddPedalForm';
import AddWantForm from '../components/AddWantForm';
import AddPedalMobileChromeHeader from '../components/AddPedalMobileChromeHeader';
import Footer from '../components/Footer';
import SellingDisabledBanner from '../components/SellingDisabledBanner';
import { useSellingDisabled } from '../hooks/useSellingDisabled';
import { prewarmReverbSuggestions } from '../hooks/useReverbProductSuggestions';
import { scrollPageToTop } from '../utils/scrollPageToTop';

/**
 * /addpedal — single host for the Add Pedal and Add Want flows.
 *
 * For wants this is now the ONLY surface: My Wants is a pure list/management
 * page; every add/edit/prefill/listing-update interaction lands here.
 *
 * Routes that land here in want mode:
 *   /addpedal?tab=want                           Plain new add (navbar / "+" button / My Wants header CTA)
 *   /addpedal?tab=want&prefill=true&make=&...    Listing-driven NEW add (PedalDetails "Save to Wantlist")
 *   /addpedal?tab=want&edit=<wantId>             Edit a saved want
 *     + state.matchListing                       Listing-driven UPDATE (conflict modal "Update")
 *     + state.editHints                          Hints to fill blank fields (e.g. effectTypes)
 *     + state.returnToListing                    Drives the listing-flow success card
 *
 * After save, the form is replaced with a success card. The card's secondary
 * action depends on whether the user came from a listing.
 */
const AddPedalPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authLoading, setAuthLoading] = useState(true);
  const { disabled: sellingDisabled } = useSellingDisabled();

  // Honor optional ?tab=want via router state OR query string. Query string
  // also lets us deep-link from PedalDetails / saved-wants Edit cleanly.
  const initialTab = (() => {
    if (location.state?.tab === 'want') return 'want';
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'want') return 'want';
    return 'pedal';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Native-only horizontal swipe between Add Pedal <-> Add Want, mirroring the
  // toggle, with a slide-in animation. NOTE: switching tabs unmounts the active
  // form, so an in-progress entry is cleared on swipe just as it is on a toggle
  // click — the 60px delta keeps casual scrolls from flipping mid-edit.
  const tabScrollCancelRef = useRef(null);
  useEffect(() => () => tabScrollCancelRef.current?.(), []);
  const tabIndex = activeTab === 'want' ? 1 : 0;
  // The add form's photo step reorders thumbnails with a horizontal drag; the
  // form-swap swipe fought it (same conflict as MyPedalsHub). Swap disabled
  // only while the form owns the gesture — elsewhere it works as before.
  const [formOwnsGesture, setFormOwnsGesture] = useState(false);
  const { swipeProps, viewClass, transitionTo } = useLocalSwipeViews(tabIndex, 2, (next) => {
    setActiveTab(next === 1 ? 'want' : 'pedal');
    if (Capacitor.isNativePlatform()) {
      // The incoming form's layout is not committed yet, and iOS restores its own
      // offset over a plain scrollTo — the keyboard is usually up on these forms.
      tabScrollCancelRef.current?.();
      tabScrollCancelRef.current = scrollPageToTop();
    }
  }, { enabled: !formOwnsGesture });
  // Toggle taps run through the same animated transition (instant on web).
  const handleTabChange = (tab) => transitionTo(tab === 'want' ? 1 : 0);

  // ── Want flow inputs (parsed from URL/state) ─────────────────────────────
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editWantId = queryParams.get('edit') || null;
  const isPrefillFlow = queryParams.get('prefill') === 'true';

  // returnToListing is captured immediately so the navigation that may later
  // strip router state can't take it from us mid-flow.
  const incomingReturnToListing = location.state?.returnToListing || null;
  const incomingMatchListing = location.state?.matchListing || null;
  const incomingEditHints = location.state?.editHints || null;

  const [returnToListing, setReturnToListing] = useState(incomingReturnToListing);
  const [matchListing, setMatchListing] = useState(incomingMatchListing);
  const [editHints, setEditHints] = useState(incomingEditHints);

  // When we have ?edit=<wantId> we fetch the want doc here and hand it to the
  // form. Loading + not-found states are surfaced inline so the user is never
  // staring at a blank form wondering what happened.
  const [editingWant, setEditingWant] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // Success card state — kept on the host (not the form) so the form can
  // unmount cleanly after save and the success card has a stable home.
  const [savedMode, setSavedMode] = useState(null); // 'add' | 'update' | null
  // Bumped to remount the form when "Add Another" is pressed, fully resetting
  // its internal state without juggling controlled values from out here.
  const [formInstanceKey, setFormInstanceKey] = useState(0);

  // Capture incoming router state into local state once (router state is not
  // sticky across replaceState calls some flows do downstream).
  useEffect(() => {
    if (incomingReturnToListing) setReturnToListing(incomingReturnToListing);
  }, [incomingReturnToListing]);
  useEffect(() => {
    if (incomingMatchListing) setMatchListing(incomingMatchListing);
  }, [incomingMatchListing]);
  useEffect(() => {
    if (incomingEditHints) setEditHints(incomingEditHints);
  }, [incomingEditHints]);

  // Redirect logged-out users to login. authedUser is real state (not a bare
  // auth.currentUser read) because the edit-want fetch below must RE-RUN when
  // auth resolves — see the bug note there.
  const [authedUser, setAuthedUser] = useState(auth.currentUser);
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setAuthLoading(false);
      setAuthedUser(user);
      if (!user) {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Prewarm the Reverb typeahead callable as soon as this page loads (covers
  // both Add Pedal and Add Want), so its container cold-starts while the user is
  // still reading the form rather than mid-keystroke.
  useEffect(() => {
    prewarmReverbSuggestions();
  }, []);

  // Re-init the active tab on a NEW navigation that carries tab info. Without
  // this, tapping "Add Pedal" → "Add Want" → "Add Pedal" from the mobile
  // bottom-sheet wouldn't reset (the route is the same).
  useEffect(() => {
    if (location.state?.tab === 'want') {
      setActiveTab('want');
    } else if (location.state?.tab === 'pedal') {
      setActiveTab('pedal');
    } else if (queryParams.get('tab') === 'want') {
      setActiveTab('want');
    }
  }, [location.key, location.state, queryParams]);

  // Fetch the want we're editing. Re-runs only when the id changes (or when
  // the user signs in/out). Auth-gated and ownership-checked.
  useEffect(() => {
    let cancelled = false;
    if (!editWantId) {
      setEditingWant(null);
      setEditError(null);
      return;
    }

    const run = async () => {
      // BUG FIX (2026-09-18): this used to read auth.currentUser and silently
      // return when it was still null (cold start / app resume / fast tap
      // after a reload) — and since auth wasn't a dependency, the fetch never
      // re-ran. Result: the edit screen rendered a BLANK Add Want form on the
      // first attempt and only worked on the second. Now: while auth is
      // unresolved we show the loading state and this effect re-runs via the
      // authedUser dependency the moment it resolves.
      const user = authedUser;
      if (!user) {
        setEditLoading(true);
        return;
      }
      setEditLoading(true);
      setEditError(null);
      try {
        const snap = await getDoc(doc(db, 'wantList', editWantId));
        if (cancelled) return;
        if (!snap.exists()) {
          setEditError('This want no longer exists.');
          setEditingWant(null);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        // Ownership check — both legacy uid + newer ownerID, like elsewhere.
        if (data.uid !== user.uid && data.ownerID !== user.uid) {
          setEditError("You don't have permission to edit this want.");
          setEditingWant(null);
          return;
        }
        // Apply editHints (e.g. listing.effectTypes) only to fields the want
        // left blank — never override the user's prior choices. Matches the
        // pre-refactor behavior.
        let merged = data;
        if (editHints) {
          const wantEffectTypes = Array.isArray(data.effectTypes) ? data.effectTypes : [];
          const hintEffectTypes = Array.isArray(editHints.effectTypes) ? editHints.effectTypes : [];
          if (wantEffectTypes.length === 0 && hintEffectTypes.length > 0) {
            merged = { ...data, effectTypes: hintEffectTypes };
          }
        }
        setEditingWant(merged);
      } catch (err) {
        console.error('Failed to load want for edit:', err);
        if (!cancelled) {
          setEditError('Could not load this want. Please try again.');
        }
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [editWantId, editHints, authedUser]);

  // Handle window resize for responsive toggle
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll up when the success card appears so the user actually sees it.
  // A bare window.scrollTo does not survive here: the card replaces a tall form
  // in the same commit, so the scroll must wait for the new layout, and the iOS
  // WebView then restores its own offset over the top of ours.
  const scrollCancelRef = useRef(null);
  useEffect(() => {
    if (!savedMode) return undefined;
    scrollCancelRef.current?.();
    scrollCancelRef.current = scrollPageToTop();
    return () => scrollCancelRef.current?.();
  }, [savedMode]);

  const handleAddAnother = () => {
    // Clear any URL params (prefill / edit) so we land in plain add mode, and
    // wipe any listing context. Bump the form key to remount with a fresh
    // empty state.
    setSavedMode(null);
    setReturnToListing(null);
    setMatchListing(null);
    setEditHints(null);
    setEditingWant(null);
    setFormInstanceKey((k) => k + 1);
    if (location.search || location.state) {
      navigate('/addpedal', { replace: true, state: { tab: 'want' } });
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="p-4 text-center" style={{ marginTop: '200px' }}>
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Listing-driven flow → blue Return link in the success card.
  const isListingDrivenFlow = !!returnToListing;

  // Body copy for the success card varies a touch by mode.
  const successHeading = savedMode === 'update' ? 'Want updated' : 'Want saved';

  return (
    <div
      className="add-pedal-page"
      style={{
        backgroundColor: '#F7F3EB',
        minHeight: 'calc(100vh - var(--navbar-height, 125px))',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* BottomTabBar adds body { padding-bottom: 150px } on mobile to clear
          the fixed tab bar. Pin a 150px beige stripe to the BOTTOM of the
          viewport (background-attachment: fixed) so the area exposed below
          the page content blends with this page's beige background instead
          of showing the white body default. Scoped to this component's
          lifetime. Same pattern used by MyPedalsHub / LatestPedals. */}
      <style jsx>{`
        body {
          background:
            linear-gradient(to bottom,
              #ffffff 0%,
              #ffffff calc(100% - 150px),
              #F7F3EB calc(100% - 150px),
              #F7F3EB 100%) fixed !important;
        }
      `}</style>
      <style>{`
        @media (max-width: 768px) {
          .add-pedal-page {
            padding: 0 !important;
          }
        }
        
        /* Hide the internal form headers since we have page-level headers */
        .add-pedal-page form.space-y-4 > h2:first-of-type {
          display: none !important;
        }
        
        .add-pedal-page .bg-white.rounded-lg.shadow-md > h2:first-of-type {
          display: none !important;
        }
      `}</style>

      <div {...swipeProps} className="add-pedal-page-content" style={{ paddingTop: '2rem', paddingLeft: '1rem', paddingRight: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <AddPedalMobileChromeHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

      <div className="swipe-clip">
      <div key={activeTab} className={viewClass}>
      {activeTab === 'pedal' ? (
        sellingDisabled ? (
          <div style={{ maxWidth: '750px', margin: '2rem auto', padding: '0 1rem', width: '100%', boxSizing: 'border-box' }}>
            <SellingDisabledBanner mode="full" />
          </div>
        ) : (
          <AddPedalForm onOwnsHorizontalGesture={setFormOwnsGesture} />
        )
      ) : (
        <>
          <style>{`
            /* Notes textarea sizing inside the "Anything else" card */
            .add-pedal-page textarea {
              min-height: 160px !important;
              height: 160px !important;
              font-size: 16px !important;
              width: 100% !important;
              box-sizing: border-box !important;
            }
          `}</style>
          {(savedMode || editLoading || editError) ? (
            <div
              style={{
                borderRadius: '12px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                backgroundColor: 'white',
                padding: isMobile ? '1.5rem' : '2rem',
                maxWidth: isMobile ? '28rem' : '750px',
                width: isMobile ? 'auto' : '100%',
                margin: isMobile ? '1rem 0.5rem' : '0 auto',
                marginTop: isMobile ? '1rem' : '2rem',
                marginBottom: '2rem'
              }}
            >
              {savedMode ? (
                <SuccessCard
                  heading={successHeading}
                  isListingDrivenFlow={isListingDrivenFlow}
                  returnToListing={returnToListing}
                  onAddAnother={handleAddAnother}
                  onGoToWantList={() => navigate('/my-pedals/wants')}
                  onReturnToListing={() => navigate(-1)}
                />
              ) : editLoading ? (
                <div style={{ padding: '1.5rem 0', textAlign: 'center', color: '#6B7280', fontFamily: 'Nunito, sans-serif' }}>
                  Loading want…
                </div>
              ) : (
                <div style={{ padding: '1.5rem 0', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
                  <p style={{ color: '#B91C1C', marginBottom: '0.75rem' }}>{editError}</p>
                  <Link to='/my-pedals/wants' style={{ color: '#5A9ECB', textDecoration: 'underline', fontWeight: 600 }}>
                    ← Back to Wantlist
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                maxWidth: isMobile ? '28rem' : '750px',
                width: isMobile ? 'auto' : '100%',
                margin: isMobile ? '1rem 0.5rem' : '0 auto',
                marginTop: isMobile ? '1rem' : '2rem',
                marginBottom: '2rem',
                boxSizing: 'border-box'
              }}
            >
              <AddWantForm
                key={`${editWantId || 'new'}-${formInstanceKey}`}
                wideSpacing={!isMobile}
                editingWant={editingWant}
                matchListing={matchListing}
                suppressSuccessAlert
                onWantAdded={() => setSavedMode('add')}
                onFinishEdit={() => setSavedMode('update')}
                onCancelPrefill={() => {
                  // Clear listing/prefill context and drop them onto Wantlist.
                  setReturnToListing(null);
                  setMatchListing(null);
                  navigate('/my-pedals/wants', { replace: true });
                }}
              />
            </div>
          )}
        </>
      )}
      </div>
      </div>

      <Footer />
      </div>
    </div>
  );
};

// Success card — used after a successful add/update of a want. Listing-driven
// flows surface a blue "Return to [pedal]" link as the primary action; everyone
// else gets "Add Another" + "Go to Wantlist".
const SuccessCard = ({ heading, isListingDrivenFlow, returnToListing, onAddAnother, onGoToWantList, onReturnToListing }) => (
  <div style={{ width: '100%', textAlign: 'center', padding: '1rem 0', fontFamily: 'Nunito, sans-serif' }}>
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '48px',
        height: '48px',
        borderRadius: '9999px',
        backgroundColor: '#4db6ac',
        marginBottom: '0.75rem'
      }}
    >
      <svg width="24" height="24" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </div>
    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1F2937', margin: '0 0 0.25rem' }}>{heading}</h2>
    <p style={{ fontSize: '0.95rem', color: '#4B5563', margin: '0 0 1.25rem', lineHeight: 1.4 }}>
      {isListingDrivenFlow
        ? 'Your Wantlist is updated. Pick up where you left off, or stay here on your wants.'
        : 'Your Wantlist is updated. Add another or head back to your wants.'}
    </p>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
      {isListingDrivenFlow ? (
        <>
          <button
            type="button"
            onClick={onReturnToListing}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: '#5A9ECB',
              fontSize: '0.95rem',
              fontWeight: 600,
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            ← Return to {returnToListing?.label || 'listing'}
          </button>
          <button
            type="button"
            onClick={onGoToWantList}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '6px 12px',
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            Go to Wantlist
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={onAddAnother}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: '#5A9ECB',
              fontSize: '0.95rem',
              fontWeight: 600,
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            + Add Another Want
          </button>
          <button
            type="button"
            onClick={onGoToWantList}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '6px 12px',
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            Go to Wantlist
          </button>
        </>
      )}
    </div>
  </div>
);

export default AddPedalPage;
